import { MIN_AUTO_REFILL_CENTS, MIN_PURCHASE_CENTS, MIN_REFILL_TRIGGER_CENTS } from './credit.ts'
import { formatCents } from './general.ts'

/**
 * What a customer reads when a money action fails (app#200).
 *
 * ⚠⚠ THE PANEL USED TO RENDER `e.message` VERBATIM, AND THAT MESSAGE IS A GO ERROR. Measured on
 * prod 2026-09-03, pressing Buy credit put this in the red alert:
 *
 *     buy credit: reserve credit purchase for org b79b185a-…: sql: Scan error on column index 0,
 *     name "begin_credit_purchase": converting driver.Value type string ("f9b326b4-…") to a
 *     uint8: invalid syntax
 *
 * One string, and it names an internal SQL function, the organization id, a reservation id, a Go
 * type and a database driver. It also answers none of the three questions a failed purchase
 * raises, in this order: was I charged, what went wrong, what do I do now.
 *
 * ⚠⚠ THE MONEY SENTENCE IS THE POINT, NOT THE TIDIER WORDING. "Your card was not charged" and "we
 * cannot confirm whether your card was charged" are different facts with different remedies, and
 * a customer cannot tell them apart from the screen. So each cause below declares which one it
 * is, and the two are never collapsed into one polite sentence.
 *
 * ⚠ THIS IS NOT A BLANKET "Something went wrong". The specific sentences the panel already gets
 * right must survive — the client-side `purchaseEntryRefusal` copy for $0.49 is the standard the
 * issue holds this to — so every cause the router can name gets its own remedy here, and the
 * fallback is reached only by a failure nobody has classified yet.
 */

/**
 * Whether the failing action could have moved money.
 *
 * `charge` is a call that charges the card on file NOW — `credit op=buy`, and nothing else on
 * these panels today. `manage` is everything around it: the billing portal, the plan picker, the
 * invoice pages, the auto-refill rule, the period maximum, the spending switch. A rule that is
 * STORED is not money that MOVED, so a failed save must not raise the question of a charge at
 * all: an unasked question is answered by silence, and a gratuitous "your card was not charged"
 * on a switch press teaches the customer that the sentence means nothing.
 */
export type MoneyAction = 'charge' | 'manage'

/** Whether the card is known to be untouched, or the outcome is genuinely not known. */
type Money = 'none' | 'unknown'

/** "Your card was not charged", in the two forms the customer needs to be able to separate. */
const MONEY_SENTENCE: Record<Money, string> = {
  none: 'Your card was not charged.',
  unknown:
    'We cannot confirm whether your card was charged. ' +
    'Check your billing history before you try again.',
}

/**
 * The causes the router can name, and the remedy for each.
 *
 * ⚠⚠ MATCHED ON THE MESSAGE, BECAUSE THE CODE CANNOT SEPARATE THEM. The router's `ErrorCode` set
 * is generic — `invalid_args`, `failed_precondition`, `internal` — and `src/tools/credit.go`
 * deliberately keeps the causes apart in the MESSAGE instead: "the refusals are separated because
 * their remedies are, and a customer who cannot tell them apart cannot act". No saved card, no
 * period maximum left and a declined card are all `invalid_args` or `internal`, so keying on the
 * code alone would put the three back in one bucket the router took trouble to split.
 *
 * ⚠ A SIGNATURE THAT MISSES COSTS A BETTER SENTENCE, NEVER A WORSE OUTCOME. The raw text is never
 * rendered: an unmatched failure falls through to the code table and then to the fallback, both
 * of which are safe to show. So a router that rewords one of these degrades the copy by one
 * step; it cannot leak.
 *
 * Order matters — the first match wins, so the specific patterns come before the general ones.
 */
const CAUSES: ReadonlyArray<{ match: RegExp; text: string; money: Money }> = [
  {
    // `billing.ErrNoPaymentMethod` — a customer who reached checkout and never finished it.
    match: /no card is saved/i,
    text: 'No card is saved for this organization. Add a card in the billing portal, then try again.',
    money: 'none',
  },
  {
    // `billing.ErrNoCustomer`, from the purchase path.
    match: /never subscribed/i,
    text: 'This organization has never subscribed, so there is no card to charge. Subscribe to a paid plan first.',
    money: 'none',
  },
  {
    // `billing.ErrNoCustomer`, from the portal path — the router words this one as its own remedy.
    match: /before opening the billing portal/i,
    text: 'Subscribe to a paid plan before you open the billing portal.',
    money: 'none',
  },
  {
    // `db.ErrSpendCapReached`. The maximum bounds automatic refills (nobgp-router#1195), and this
    // refusal is the one place a manual purchase meets it.
    match: /period maximum/i,
    text:
      'This purchase is more than the period maximum permits. ' +
      'Raise the monthly maximum, or buy a smaller amount.',
    money: 'none',
  },
  {
    // `billing.ErrCreditNotEnabled` — the state of prod until credit billing is switched on.
    match: /billed on its invoice|bills usage in arrears/i,
    text:
      'This organization pays for usage past its allowance on its invoice, not from a credit balance. ' +
      'There is no credit to buy yet.',
    money: 'none',
  },
  {
    // `billing.ErrNotConfigured`. Nothing the customer can do, so do not ask them to do anything.
    match: /billing is not configured/i,
    text: 'Billing is not available at the moment. Try again later.',
    money: 'none',
  },
  {
    // `billing.ErrNoSubscription` — a portal deep link with nothing to act on.
    match: /no subscription/i,
    text: 'This organization has no subscription, so there is nothing for this action to change.',
    money: 'none',
  },
  {
    // `billing.ErrUnknownFlow`. A wrong `flow` is OUR bug, not the customer's, so it names no
    // remedy they could apply and the support reference below is what carries it.
    match: /flow must be one of/i,
    text: 'We could not open that page of the billing portal.',
    money: 'none',
  },
  {
    // `buyCredit`'s minimum. The typed-amount check in `credit.ts` normally refuses this before
    // the call, so reaching here means the field was bypassed; the number is quoted from the same
    // constant either way.
    match: /amount_cents must be at least/i,
    text: `The smallest purchase is ${formatCents(MIN_PURCHASE_CENTS)}. Enter a larger amount.`,
    money: 'none',
  },
  {
    // `setAutoReload`'s AUTOMATIC floor, which is a different number from the arm above and is
    // refused for a different reason: the card network accepts 50 cents, and what we refuse is a
    // charge whose fee is most of itself (nobgp-router#1333). Reached whenever the form is
    // bypassed — a stale client, an MCP or a REST caller — and measured on prod 2026-09-07 05:47
    // from the form itself, where it read as the `internal` fallback (app#295).
    //
    // ⚠ IT RUNS AFTER THE `amount_cents` ARM AND NEITHER SHADOWS THE OTHER. The two patterns are
    // anchored on the FIELD NAME, and the router names exactly one field per refusal, so no
    // message can match both. The order between them is therefore free; they sit together because
    // they are the pair a reader must not conflate.
    // ⚠⚠ THE NUMBER IS IN THE PATTERN, AND THAT IS WHAT MAKES THE SENTENCE SAFE. Router
    // v0.4.135 and earlier refused this field at `MinPurchaseCents` and said so — "reload_cents
    // must be at least 50 — a smaller purchase is refused by the card network" — so a pattern
    // anchored on the field name alone matches that too and answers a $0.50 floor with the $5.00
    // sentence. Wrong money, told to the customer. Built from the constant, so the two can never
    // disagree: if the router's floor moves, this arm stops matching and the reader gets the
    // generic sentence, which is the degradation this file already accepts — "a signature that
    // misses costs a better sentence, never a worse outcome".
    match: new RegExp(`reload_cents must be at least ${MIN_AUTO_REFILL_CENTS}\\b`, 'i'),
    text:
      `Each automatic refill must be at least ${formatCents(MIN_AUTO_REFILL_CENTS)} — ` +
      'a smaller one costs more in card fees than it is worth. ' +
      `Raise the amount, or buy as little as ${formatCents(MIN_PURCHASE_CENTS)} yourself with Buy credit.`,
    money: 'none',
  },
  {
    // The sibling refusal on the other field of the same rule. Same reasoning as above: keyed on
    // `trigger_cents`, so it cannot be matched by, or match, either amount arm.
    match: new RegExp(`trigger_cents must be at least ${MIN_REFILL_TRIGGER_CENTS}\\b`, 'i'),
    text:
      `The balance to buy below must be at least ${formatCents(MIN_REFILL_TRIGGER_CENTS)}. ` +
      'A trigger of $0.00 could never fire, because a balance cannot fall below zero.',
    money: 'none',
  },
  {
    // A card that wants a confirmation step — 3-D Secure is the usual one. `PurchaseCredit`
    // releases the reservation on this path, so the card is known to be untouched.
    //
    // ⚠⚠ THE CONFIRMATION ARM RUNS BEFORE THE DECLINE ARM, AND THE ORDER IS THE WHOLE OF ITS
    // CORRECTNESS (app#279). Stripe reports a 3-D Secure demand as a `card_error` whose code is
    // `authentication_required` and whose message reads "Your card was declined. This transaction
    // requires authentication." — so the live error carries BOTH words, and the decline arm
    // below matches it too. With the decline arm first, this sentence was unreachable for exactly
    // the failure it was written for: measured 2026-09-06 06:57 UTC on a `tok_threeDSecure2Required`
    // card, the alert said "declined" while the purchase row beneath it — mapped by the router's
    // `creditFailureText` in `src/tools/credit.go`, which orders these two arms this way and
    // says so — said "needs an extra confirmation step". Same click, two causes, about a bank
    // that refused nothing. A plain decline (`insufficient_funds`, `lost_card`) matches none of
    // these four and still falls through to the decline arm.
    match: /not succeeded|requires_action|requires_confirmation|authentication_required/i,
    text:
      'Your bank asked for a confirmation that this page cannot collect. ' +
      'Use a different card in the billing portal, or contact your bank.',
    money: 'none',
  },
  {
    // A card the network refused. `PurchaseCredit` releases the reservation on this path, so the
    // card is known to be untouched — which is the whole reason to detect it rather than let it
    // fall through to `internal` and be reported as an unknown outcome.
    //
    // `"type":"card_error"` is the router's own decline signature (`creditFailureText`), and it is
    // what makes the arm complete: Stripe's wording is "Your card has expired." (`expired_card`),
    // `processing_error`, `incorrect_number` and the rest name neither "declined" nor a listed
    // code, and every one of them released the reservation (app#284). Safe only because the
    // confirmation arm above runs first — a 3-D Secure demand is a `card_error` too.
    match: /declined|insufficient funds|expired[ _]card|incorrect_cvc|card_declined|"type":"card_error"/i,
    text: 'Your card was declined. Use a different card in the billing portal, then try again.',
    money: 'none',
  },
]

/**
 * The answer when no cause matched, keyed on the router's error code.
 *
 * ⚠⚠ THE CODE IS WHAT DECIDES THE MONEY SENTENCE, and the split is not cosmetic. `invalid_args`,
 * `failed_precondition` and the permission codes are refusals the router makes BEFORE it calls
 * Stripe, so the card is untouched and we may say so. `internal` is not: `buyCredit` wraps its
 * default arm as `internal`, and that arm covers both the reserve step (before the charge) and
 * `CompleteCreditPurchase` (after it — the router's own comment says "the customer has paid and
 * holds nothing"). One code, both sides of the charge, so the honest answer is that we do not
 * know. `timeout` is the same shape.
 */
const BY_CODE: Record<string, { text: string; money: Money }> = {
  // Thrown by `callTool` before any request leaves the browser.
  unauthenticated: { text: 'You are signed out. Sign in again, then try again.', money: 'none' },
  unauthorized: { text: 'You are signed out. Sign in again, then try again.', money: 'none' },
  forbidden: { text: 'Only the organization Owner can change billing.', money: 'none' },
  permission_denied: { text: 'Only the organization Owner can change billing.', money: 'none' },
  not_found: { text: 'We could not find the billing record for this organization.', money: 'none' },
  rate_limited: { text: 'Too many requests. Wait a moment, then try again.', money: 'none' },
  invalid_args: { text: 'We could not complete this request.', money: 'none' },
  failed_precondition: {
    text: 'This action is not available for this organization at the moment.',
    money: 'none',
  },
}

/** Reached by `internal`, `timeout`, a network failure and any code nobody has classified. */
const FALLBACK = { text: 'We could not complete this request.', money: 'unknown' as Money }

/** What a thrown value carries, read structurally so ANY throw is handled and none is trusted. */
const failureOf = (thrown: unknown): { code: string; message: string; callId: string } => {
  const bag = (typeof thrown === 'object' && thrown !== null ? thrown : {}) as Record<string, unknown>
  return {
    code: typeof bag.code === 'string' ? bag.code : '',
    message: typeof bag.message === 'string' ? bag.message : '',
    callId: typeof bag.callId === 'string' ? bag.callId : '',
  }
}

/**
 * The sentence to put in front of the customer for a failed money action.
 *
 * ⚠⚠ THE CALLER MUST LOG THE THROWN VALUE ITSELF. This returns nothing of the original text, on
 * purpose — that is the defect — so `console.error(e)` beside the call is the only place the
 * driver error survives for whoever has to debug it.
 *
 * ⚠ The support reference is the router's `call_id`, and it is NOT shortened. It is the id the
 * router logs and the id `feedback_submit` joins on, so a prefix would be prettier and would not
 * find the row. A handle nobody can look up is not a remedy.
 */
export const describeBillingError = (thrown: unknown, action: MoneyAction): string => {
  const { code, message, callId } = failureOf(thrown)

  // ⚠ An OWN-PROPERTY test, not a bare index. `code` is read off a thrown value and is not ours to
  // trust — `BY_CODE['constructor']` answers `Object` from the prototype chain, which is truthy,
  // so `??` never reaches the fallback and the destructuring yields `undefined` for both fields.
  // Every part of the sentence is then dropped by the `filter(Boolean)` below and the customer
  // reads an EMPTY alert on a failed money action, which is the one outcome this file exists to
  // prevent.
  const cause = CAUSES.find(c => c.match.test(message))
  const known = Object.prototype.hasOwnProperty.call(BY_CODE, code) ? BY_CODE[code] : undefined
  const { text, money } = cause ?? known ?? FALLBACK

  return [text, action === 'charge' ? MONEY_SENTENCE[money] : '', callId ? `Support reference: ${callId}.` : '']
    .filter(Boolean)
    .join(' ')
}
